'use client'

import {
  resetPassword as amplifyResetPassword,
  confirmResetPassword,
  confirmSignIn,
  signIn,
  signOut,
} from 'aws-amplify/auth'

import type {
  ForgotPasswordFormData,
  ForgotPasswordFormState,
  LoginFormData,
  LoginFormState,
  ResetPasswordFormState,
  ResetPasswordMutationData,
  UpdatePasswordFormData,
  UpdatePasswordFormState,
} from './auth.types'

/**
 * Forgot Password Action to be used with useActionState
 */
export async function forgotPassword(
  _prevState: ForgotPasswordFormState,
  values: ForgotPasswordFormData,
): Promise<ForgotPasswordFormState> {
  try {
    await amplifyResetPassword({ username: values.email.trim() })
    return { success: true, errorMessage: null, email: values.email }
  } catch (error) {
    console.error(error)
    return {
      success: false,
      errorMessage: 'forgot-password.form.submit.error.failed',
      email: values.email,
    }
  }
}

/**
 * Reset Password Mutation
 */
export async function resetPassword(
  _prevState: ResetPasswordFormState,
  values: ResetPasswordMutationData,
): Promise<ResetPasswordFormState> {
  try {
    await confirmResetPassword({
      username: values.username,
      confirmationCode: values.code,
      newPassword: values.password,
    })

    return {
      success: true,
      errorMessage: null,
      code: values.code,
      password: values.password,
      confirmPassword: values.confirmPassword,
    }
  } catch (error) {
    console.error(error)

    return {
      success: false,
      errorMessage: 'reset-password.form.submit.error.failed',
      code: values.code,
      password: values.password,
      confirmPassword: values.confirmPassword,
    }
  }
}

/**
 * Login Mutation
 */
export async function login(
  _prevState: LoginFormState,
  values: LoginFormData,
): Promise<LoginFormState> {
  try {
    try {
      await signOut()
    } catch (error) {
      console.error(error)
    }

    const { isSignedIn, nextStep } = await signIn({
      username: values.username,
      password: values.password,
    })

    if (
      !isSignedIn &&
      nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    ) {
      return {
        success: true,
        errorMessage: null,
        username: values.username,
        nextStep: 'updatePassword',
      }
    }

    if (isSignedIn && nextStep?.signInStep === 'DONE') {
      return {
        success: true,
        errorMessage: null,
        username: values.username,
        nextStep: 'done',
      }
    }

    return {
      success: false,
      errorMessage: 'login.form.submit.error.failed',
      username: values.username,
    }
  } catch (error) {
    console.error(error)
    return {
      success: false,
      errorMessage: 'login.form.submit.error.failed',
      username: values.username,
    }
  }
}

/**
 * Update Password Mutation
 */
export async function updatePassword(
  _prevState: UpdatePasswordFormState,
  values: UpdatePasswordFormData,
): Promise<UpdatePasswordFormState> {
  try {
    const { isSignedIn, nextStep } = await confirmSignIn({
      challengeResponse: values.password,
    })

    if (isSignedIn && nextStep.signInStep === 'DONE') {
      return {
        success: true,
        errorMessage: null,
        password: values.password,
        confirmPassword: values.confirmPassword,
      }
    }

    return {
      success: false,
      errorMessage: 'update-password.form.submit.error.failed',
      password: values.password,
      confirmPassword: values.confirmPassword,
    }
  } catch (error) {
    console.error(error)
    return {
      success: false,
      errorMessage: 'update-password.form.submit.error.failed',
      password: values.password,
      confirmPassword: values.confirmPassword,
    }
  }
}
